export type HeapAPI = {
  isHeapReady: (timeout: number) => Promise<boolean>;
  identifyUser: (identity: string) => void;
  addEventProperties: (props: Record<string, string | number>) => void;
  removeEventProperty: (property: string) => void;
};

const CouldNotLoadHeapError = new Error('Could not load Heap');
CouldNotLoadHeapError.name = 'HeapCouldNotLoadError';

const HeapNotReadyError = new Error('Heap did not emit a Ready event');
HeapNotReadyError.name = 'HeapDidNotReturnReady';

const HeapCouldNotIdentifyUser = new Error('Could not identify user with Heap');
HeapCouldNotIdentifyUser.name = 'HeapCouldNotIdentifyUser';

const CouldNotAddEventProperties = new Error('Could not add event properties to Heap');
CouldNotAddEventProperties.name = 'HeapCouldNotAddEventProperties';

const CouldNotRemoveEventProperty = new Error('Could not remove event property from Heap');
CouldNotRemoveEventProperty.name = 'HeapCouldNotRemoveEventProperty';

const HEAP_HAS_LOADED_INTERVAL_MS = 500;

export const HeapEventPropertyKeys = {
  CKO_USER_ID: 'CkoUserId',
  ROLE: 'Role',
  USER_TYPE: 'UserType',
  CLIENT_ID: 'ClientID',
  CLIENT_NAME: 'ClientName',
  ENTITY_ID: 'EntityID',
  ENTITY_NAME: 'EntityName',
  SEGMENT_ID: 'SegmentID',
  SEGMENT_NAME: 'SegmentName',
  SUB_ENTITY_ID: 'SubEntityID',
  SUB_ENTITY_NAME: 'SunEntityName',
};

export const getHeapAPI = (addError: (error: Error) => void): HeapAPI => {
  return {
    isHeapReady: async (timeout: number) => {
      const getTimeoutPromise = () =>
        new Promise<boolean>((resolve) => {
          setTimeout(() => {
            resolve(false);
          }, timeout);
        });

      const isLoadedPromise = new Promise<boolean>((resolve) => {
        const isLoadedInterval = setInterval(() => {
          if (window.heap?.loaded) {
            clearInterval(isLoadedInterval);
            resolve(true);
          }
        }, HEAP_HAS_LOADED_INTERVAL_MS);

        getTimeoutPromise().then(() => {
          clearInterval(isLoadedInterval);
        });
      });

      const onReadyPromise = new Promise<boolean>((resolve) => {
        try {
          window.heap?.onReady(() => {
            resolve(true);
          });
        } catch {
          addError(HeapNotReadyError);
          resolve(false);
        }
      });

      const isLoaded = await Promise.race([isLoadedPromise, getTimeoutPromise()]);

      if (!isLoaded) {
        addError(CouldNotLoadHeapError);
        return false;
      }

      const isReady = await Promise.race([onReadyPromise, getTimeoutPromise()]);

      if (!isReady) {
        addError(HeapNotReadyError);
        return false;
      }
      return true;
    },
    addEventProperties: (props: Record<string, string | number>) => {
      try {
        window.heap?.addEventProperties(props);
      } catch {
        //could not add event properties , eat error to prevent app from crashing also.
        addError(CouldNotAddEventProperties);
      }
    },
    removeEventProperty: (property: string) => {
      try {
        window.heap?.removeEventProperty(property);
      } catch {
        //could not add event properties , eat error to prevent app from crashing also.
        addError(CouldNotRemoveEventProperty);
      }
    },
    identifyUser: (identity: string) => {
      try {
        window.heap?.identify(identity);
      } catch {
        //could not identify user, eat error to prevent app from crashing also.
        addError(HeapCouldNotIdentifyUser);
      }
    },
  };
};
